import {
  buildDateRangeApiParams,
  buildExactDateApiParams,
  type GenericCrudTableDateColumnDef,
  isAnyDateRangeFilterValue,
  isCommittedDateRangeFilterValue,
  isDateRangeValueEmpty,
  isDateRangeValueInvalid,
  normalizeDateRangeValue,
  resolveDateFilterFieldPath,
  resolveFilterDateRangeFormat,
  supportsDateRangeApiFiltering,
} from "@/components/dateTime/genericDateFilterUtils";

export type MrtColumnFilter = {
  id: string;
  value: unknown;
};

export const hasIncompleteMrtDateFilter = (
  columnFilters: MrtColumnFilter[],
  columnDefs: GenericCrudTableDateColumnDef[],
  allowsDateRangeFilters = true
): boolean =>
  columnFilters.some((filter) => {
    const columnDef = columnDefs.find((col) => col.id === filter.id);
    if (!columnDef || columnDef.filterVariant !== "date") {
      return false;
    }

    const value = filter.value;

    if (
      supportsDateRangeApiFiltering(columnDef, allowsDateRangeFilters) &&
      Array.isArray(value)
    ) {
      const range = normalizeDateRangeValue(value);
      if (isDateRangeValueEmpty(range)) {
        return false;
      }
      if (!isCommittedDateRangeFilterValue(value)) {
        return true;
      }
      return isDateRangeValueInvalid(range);
    }

    if (value === undefined || value === null || String(value).trim() === "") {
      return false;
    }

    return Number.isNaN(new Date(value as string).getTime());
  });

export const buildSimpleMrtColumnFilterApiParams = (
  columnFilters: MrtColumnFilter[],
  columnDefs: GenericCrudTableDateColumnDef[]
): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {};

  columnFilters.forEach((filter) => {
    const column = filter.id;
    const value = filter.value;
    const columnDef =
      columnDefs.find((col) => col.id === column) ??
      ({ id: column } as GenericCrudTableDateColumnDef);

    const customFilterId = columnDef.customColumnFilterId || "";
    const filterVariant = columnDef.filterVariant || "";
    const customParam = columnDef.customFilterParameter || "";
    const useGenericParam = columnDef.setNoFilterParameter !== true;

    if (filterVariant === "date") {
      if (
        supportsDateRangeApiFiltering(columnDef) &&
        isAnyDateRangeFilterValue(value)
      ) {
        const range = normalizeDateRangeValue(value);
        if (isDateRangeValueEmpty(range)) {
          return;
        }
        Object.assign(
          params,
          buildDateRangeApiParams(
            resolveDateFilterFieldPath(columnDef, column),
            range,
            resolveFilterDateRangeFormat(columnDef),
            columnDef.dateFilterApiParams
          )
        );
        return;
      }

      Object.assign(
        params,
        buildExactDateApiParams(
          resolveDateFilterFieldPath(columnDef, column),
          value,
          {
            customFilterId,
            columnId: column,
            customParam,
            useGenericParam,
            dateFilterApiParams: columnDef.dateFilterApiParams,
          }
        )
      );
      return;
    }

    if (customFilterId) {
      params[`${customFilterId}__icontains`] = String(value);
      return;
    }

    params[`${column}__icontains`] = String(value);
  });

  return params;
};

export const canUseMrtColumnFilters = (
  subdomain: string,
  allowColumnFiltering?: boolean
): boolean =>
  allowColumnFiltering ?? (subdomain === "admin" || subdomain === "manage");
